import { Button, Container, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ActivePane, useAppStateContext } from '../../AppStateProvider/AppStateProvider';
import { CheckMark } from '../../../icons/CheckMark';
import { DownloadIcon } from '../../../icons/DownloadIcon';
import { getQualityScore } from '../Quality/getQualityScore/getQualityScore';
import { QualityScore } from '../Quality/Quality';
import { SmallError } from '../../../icons/SmallError';
import SomeFailed from './SomeFailedNew.png';
import TestsPassed from './TestsPassedNew.png';

const Header = styled(Grid)(({ theme }) => ({
  float: 'left',
  [theme.breakpoints.down('md')]: {
    float: 'initial',
    paddingBottom: '1em',
  },
}));

const ButtonContainer = styled('div')({
  display: 'inline-flex',
  flexWrap: 'wrap',
  gap: '1em',
  width: '100%',
});

const ResultsList = styled(Grid)(({ theme }) => ({
  float: 'right',
  [theme.breakpoints.down('md')]: {
    float: 'initial',
  },
}));

const ResultContainer = styled('div')(({ theme }) => ({
  marginTop: '1.5em',
  '&:not(:last-child)': {
    paddingBottom: '1.5em',
    borderBottom: '0.1em solid #8891AA',
  },
  [theme.breakpoints.down('md')]: {
    '&:last-child': {
      paddingBottom: '1.5em',
    },
  },
}));

const IconContainer = styled('div')({
  display: 'flex',
  '& svg': {
    margin: '0.2em 0.8em 0 0',
  },
});

const DownloadButton = styled(Button)({
  '& svg': {
    position: 'relative',
    left: '-5px',
  },
});

const RestartButton = styled(Button)({
  backgroundColor: '#FFFFFF',
  borderColor: '#8891AA',
});

const Illustration = styled('img')(({ theme }) => ({
  marginTop: '7em',
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

const GutterBottomTypography = styled(Typography)(({ theme }) => ({
  marginBottom: '1em',
  [theme.breakpoints.down('md')]: {
    marginBottom: '1.5em',
  },
}));

export function Results() {
  const { state, downloadFinalTestResults, dispatch } = useAppStateContext();
  const { totalQualityScore } = getQualityScore(state.preflightTest.report, state.bitrateTest.report);

  const testsPassed = totalQualityScore === QualityScore.Excellent || totalQualityScore === QualityScore.Good;
  const qualityScore = QualityScore[totalQualityScore].toLowerCase();

  return (
    <>
      <Container>
        <div>
          <Header item lg={5}>
            <Typography variant="h1" gutterBottom>
              {testsPassed ? 'All tests passed!' : 'Some tests failed'}
            </Typography>

            {testsPassed ? (
              <Typography variant="body1" gutterBottom>
                As far as we can tell, your video should be working. If you're still experiencing issues, download your
                report results and send to your network administrator.
              </Typography>
            ) : (
              <Typography variant="body1" gutterBottom>
                <strong>One out of three </strong>
                tests failed – use this list to solve common video issues and restart the test. If you can’t easily
                solve the problem(s), download report results and send to your network administrator.
              </Typography>
            )}

            <ButtonContainer>
              <DownloadButton
                variant="contained"
                color="primary"
                onClick={downloadFinalTestResults}
              >
                <DownloadIcon />
                Download report results
              </DownloadButton>
              <RestartButton variant="outlined" onClick={() => window.location.reload()}>
                Restart test
              </RestartButton>
            </ButtonContainer>

            <Illustration
              src={testsPassed ? TestsPassed : SomeFailed}
              alt={testsPassed ? 'Success' : 'Some Failed'}
            />
          </Header>

          <ResultsList item lg={5}>
            <ResultContainer>
              <IconContainer>
                <CheckMark />
                <GutterBottomTypography variant="h3">
                  Device &amp; Network Setup
                </GutterBottomTypography>
              </IconContainer>
              <GutterBottomTypography variant="body1">
                Audio and video successfully received from your hardware and browser.
              </GutterBottomTypography>

              <ButtonContainer>
                <Button
                  variant="outlined"
                  onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.CameraTest })}
                >
                  Review hardware
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.BrowserTest })}
                >
                  Review browser
                </Button>
              </ButtonContainer>
            </ResultContainer>

            <ResultContainer>
              <IconContainer>
                <CheckMark />
                <GutterBottomTypography variant="h3">
                  Connectivity
                </GutterBottomTypography>
              </IconContainer>
              <GutterBottomTypography variant="body1">
                All connections are working successfully.
              </GutterBottomTypography>
              <Button
                variant="outlined"
                onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.Connectivity })}
              >
                Review connectivity
              </Button>
            </ResultContainer>

            <ResultContainer>
              <IconContainer>
                {testsPassed ? <CheckMark /> : <SmallError />}
                <GutterBottomTypography variant="h3">
                  Quality &amp; Performance
                </GutterBottomTypography>
              </IconContainer>

              {testsPassed ? (
                <GutterBottomTypography variant="body1">
                  Awesome! Your expected call quality is <strong>{qualityScore}</strong> and overall performance looks
                  {qualityScore === 'excellent' ? ' good' : ' ok'}.
                </GutterBottomTypography>
              ) : (
                <GutterBottomTypography variant="body1">
                  Your overall score is <strong>{qualityScore}</strong> which means that your connection isn't good
                  enough to run video properly. Try out these tips and rerun the test.
                </GutterBottomTypography>
              )}

              <Button
                variant="outlined"
                onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.Quality })}
              >
                Review performance
              </Button>
            </ResultContainer>
          </ResultsList>
        </div>
      </Container>
    </>
  );
}
